import React, { useState, useRef, useEffect } from "react";
import TensaiGame from "./TensaiGame";
import settings from '../../core/settings';
import { getSkin } from './tileSkins';

const COLORS = {
  bg: "#2c2f34", // neutral grey background
  score: "#e0e0e0", // light text
  grid: "#393c41", // grey grid
  selected: "#60b43c",
  timer: "#3cb460",
  zen: "#3cb4b4",
};

function Tensai({ rows = 10, cols = 17, seed = null, zenMode = false, onScore, onGameEnd, timerSeconds = 120, autoStart = false }) {
  const [game, setGame] = useState(() => new TensaiGame({ rows, cols, seed }));
  const [, forceUpdate] = useState(0); // for manual re-render
  const [selected, setSelected] = useState([]);
  const [dragStart, setDragStart] = useState(null); // pixel position
  const [dragEnd, setDragEnd] = useState(null); // pixel position
  const draggingRef = useRef(false);
  const [timer, setTimer] = useState(timerSeconds);
  const [timerActive, setTimerActive] = useState(autoStart);
  const [zen, setZen] = useState(zenMode);
  const [seedInput, setSeedInput] = useState(seed ? seed.toString() : "");
  const timerRef = useRef();
  const prevScoreRef = useRef(0);
  const endSentRef = useRef(false);
  const [gridScale, setGridScale] = useState(() => settings.getNumber(settings.KEYS.TENSAI_GRID_SCALE));
  const [clearEffect, setClearEffect] = useState(() => settings.getBool(settings.KEYS.CLEAR_EFFECT));
  const [hideEmptyTiles, setHideEmptyTiles] = useState(() => settings.getBool(settings.KEYS.HIDE_EMPTY_TILES));
  const [tileSkinId, setTileSkinId] = useState(() => settings.getString(settings.KEYS.TILE_SKIN));
  const [flyingTiles, setFlyingTiles] = useState([]);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(null);
  const flyingTilesRef = useRef(flyingTiles);
  const stepRef = useRef(null);

  // Timer logic
  useEffect(() => {
    if (!zen && timerActive && timer > 0) {
      timerRef.current = setInterval(() => {setTimer((t) => (t > 0 ? t - 1 : 0))}, 1000);
      return () => clearInterval(timerRef.current);
    }
    return () => {};
  }, [timerActive, timer, zen]);

  // Reset timer to timerSeconds when new game starts
  useEffect(() => {
    setTimer(timerSeconds);
  }, [timerSeconds, game]);

  // Handle timer expiration
  useEffect(() => {
    if (timer === 0 && !zen) setTimerActive(false);
  }, [timer, zen]);

  useEffect(() => {
    if (!zen && timer === 0 && !endSentRef.current) {
      endSentRef.current = true;
      // play an end-game sound (best-effort)
      (async () => {
        try {
          const master = settings.getNumber(settings.KEYS.MASTER);
          const sfx = settings.getNumber(settings.KEYS.SFX);
          const vol = (master/100) * (sfx/100);
          const AudioManager = await import('../../core/AudioManager');
          await AudioManager.initAudio();
          AudioManager.playSound('complete.flac', { volume: vol });
        } catch (e) {
          // ignore any audio errors
        }
      })();
      if (onGameEnd) {
        try {
          const snapshot = {
            score: game.score,
            grid: game.grid,
            seed: game.seed,
          };
          onGameEnd(snapshot);
        } catch (e) {
          try { onGameEnd(); } catch (e) {}
        }
      }
    }
  }, [timer, zen, onGameEnd, game.grid, game.score, game.seed]);

  // Handle score callback - send only the delta (points earned)
  useEffect(() => {
    if (onScore) {
      if (game.score > prevScoreRef.current) {
        const pointsEarned = game.score - prevScoreRef.current;
        onScore(pointsEarned);
        prevScoreRef.current = game.score;
      } else if (game.score < prevScoreRef.current) {
        // Score decreased (e.g., game reset without going through normal reset handlers)
        prevScoreRef.current = game.score;
      }
    }
  }, [game.score, onScore]);

  // Mouse events for drag selection (pixel-based)
  const gridRef = useRef();
  const CELL_SIZE = 48;
  const CELL_MARGIN = 4;
  const CLICK_BUFFER = 24; // extra clickable buffer (px) around the grid
  const HORIZONTAL_INCREASE = 10; // add extra px to left and right
  const speedMultiplier = 2.6;
  const leftBuffer = CLICK_BUFFER + HORIZONTAL_INCREASE;
  const rightBuffer = CLICK_BUFFER + HORIZONTAL_INCREASE;

  // update gridScale when storage or custom event fires
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === settings.KEYS.TENSAI_GRID_SCALE) {
        try { setGridScale(Number(e.newValue || 100)); } catch { setGridScale(100); }
      }
    };
    const onCustom = (ev) => {
      try { setGridScale(Number(ev.detail || settings.getNumber(settings.KEYS.TENSAI_GRID_SCALE) || 100)); } catch { setGridScale(100); }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('tensaiGridScaleChanged', onCustom);
    // speedMultiplier is fixed (2.6) — no listeners
    // listen for clearEffect changes
    const onStorageClear = (e) => {
      if (e.key === 'clearEffect') {
        try { setClearEffect(e.newValue !== 'false'); } catch { setClearEffect(true); }
      }
    };
    const onCustomClear = (ev) => {
      try { setClearEffect(Boolean(ev.detail)); } catch { setClearEffect(true); }
    };
    // listen for hide-empty-tiles setting
    const onStorageHide = (e) => {
      if (e.key === 'hideEmptyTiles') {
        try { setHideEmptyTiles(e.newValue === 'true'); } catch { setHideEmptyTiles(false); }
      }
    };
    const onCustomHide = (ev) => {
      try { setHideEmptyTiles(Boolean(ev.detail)); } catch { setHideEmptyTiles(false); }
    };
    window.addEventListener('storage', onStorageClear);
    window.addEventListener('clearEffectChanged', onCustomClear);
    // listen for tile-skin setting
    const onStorageSkin = (e) => {
      if (e.key === settings.KEYS.TILE_SKIN) {
        try { setTileSkinId(e.newValue || 'basic'); } catch { setTileSkinId('basic'); }
      }
    };
    const onCustomSkin = (ev) => {
      try { setTileSkinId(String(ev.detail) || 'basic'); } catch { setTileSkinId('basic'); }
    };
    window.addEventListener('storage', onStorageSkin);
    window.addEventListener('tileSkinChanged', onCustomSkin);
    window.addEventListener('storage', onStorageHide);
    window.addEventListener('hideEmptyTilesChanged', onCustomHide);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('tensaiGridScaleChanged', onCustom);
      window.removeEventListener('storage', onStorageClear);
      window.removeEventListener('clearEffectChanged', onCustomClear);
      window.removeEventListener('storage', onStorageHide);
      window.removeEventListener('hideEmptyTilesChanged', onCustomHide);
      window.removeEventListener('storage', onStorageSkin);
      window.removeEventListener('tileSkinChanged', onCustomSkin);
      // no speedMultiplier listeners to remove
    };
  }, []);

  // Drag selection logic: track globally
  useEffect(() => {
    if (!dragStart) return;
    const scale = (gridScale || 100) / 100;
    const handleMove = (e) => {
      if (!draggingRef.current) return;
      const rect = gridRef.current.getBoundingClientRect();
      const x = e.type.startsWith("touch") ? e.touches[0].clientX : e.clientX;
      const y = e.type.startsWith("touch") ? e.touches[0].clientY : e.clientY;
      // convert to unscaled coordinates; account for clickable buffer padding
      const end = [(x - rect.left - leftBuffer) / scale, (y - rect.top - CLICK_BUFFER) / scale];
      setDragEnd(end);
      // Calculate drag rectangle
      const [x0, y0] = dragStart;
      const [x1, y1] = end;
      const left = Math.min(x0, x1);
      const top = Math.min(y0, y1);
      const right = Math.max(x0, x1);
      const bottom = Math.max(y0, y1);
      let sel = [];
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const cellX = c * (CELL_SIZE + CELL_MARGIN);
          const cellY = r * (CELL_SIZE + CELL_MARGIN);
          // Cell rectangle
          const cellRect = {
            left: cellX,
            top: cellY,
            right: cellX + CELL_SIZE,
            bottom: cellY + CELL_SIZE,
          };
          // Check overlap
          if (
            cellRect.right > left &&
            cellRect.left < right &&
            cellRect.bottom > top &&
            cellRect.top < bottom
          ) {
            sel.push([r, c]);
          }
        }
      }
      setSelected(sel);
    };
    const handleUp = async () => {
      // mark not dragging immediately to prevent any in-flight mousemove handlers
      draggingRef.current = false;
      if (selected.length > 0) {
        // capture values/positions before clearing so we can animate them
        const snapshot = selected.map(([r, c]) => ({
          r,
          c,
          value: game.grid[r] ? game.grid[r][c] : 0, // eslint-disable-line security/detect-object-injection -- 2D grid access with destructured numeric row/col indices
          left: c * (CELL_SIZE + CELL_MARGIN),
          top: r * (CELL_SIZE + CELL_MARGIN),
        }));

        const cleared = game.clearSquares(selected);
        if (cleared) {
          forceUpdate((v) => v + 1);

          // spawn flying tiles if effect enabled
          try {
            if (clearEffect) {
              const baseSpeed = 495; // px/s base (increased 50% from 330)
              const tiles = snapshot
                .filter(s => s.value)
                .map((s) => {
                  // pick a short desired upward time (seconds) so tiles fall quickly
                  const tUp = 0.12 + Math.random() * 0.33; // 0.12 .. 0.45s
                  const vyDesired = - (600) * tUp; // target initial vy (uses current gravity value)

                  // angle between 20 and 160 degrees to avoid near-horizontal extremes
                  const angleDeg = 20 + Math.floor(Math.random() * 141); // 20..160
                  const angle = (angleDeg * Math.PI) / 180;

                  // per-tile max speed based on velScale (1-5)
                  const velScale = 1 + Math.floor(Math.random() * 5);
                  const maxSpeed = baseSpeed * (velScale / 3);

                  // required speed to achieve desired vertical component: |vyDesired| / sin(angle)
                  const sinA = Math.max(Math.sin(angle), 0.2);
                  let required = Math.abs(vyDesired) / sinA;
                  // clamp to maxSpeed
                  const speed = Math.min(required, maxSpeed);

                  const vx = speed * Math.cos(angle) * speedMultiplier;
                  const vy = -speed * Math.sin(angle) * speedMultiplier; // negative = up

                  return {
                    id: `${Date.now()}-${Math.random()}`,
                    ...s,
                    x: 0,
                    y: 0,
                    vx,
                    vy,
                    rot: Math.random() * 40 - 20,
                    rotSpeed: (Math.random() * 240 - 120) * speedMultiplier, // deg/s scaled with speed
                  };
                });
              if (tiles.length > 0) {
                setFlyingTiles((prev) => [...prev, ...tiles]);
              }
            }
          } catch (e) {
            // ignore animation errors
          }

          // play selected sound if enabled
          try {
            const selectedSound = settings.getString(settings.KEYS.SELECTED_SOUND);
            const master = settings.getNumber(settings.KEYS.MASTER);
            const sfx = settings.getNumber(settings.KEYS.SFX);
            if (selectedSound) {
              const vol = (master/100) * (sfx/100);
              const AudioManager = await import('../../core/AudioManager');
              await AudioManager.initAudio();
              // try to play via AudioManager (preloads if needed)
              AudioManager.playSound(selectedSound, { volume: vol });
            }
          } catch (e) {
            // ignore
          }
        }
        setSelected([]);
      }
      setSelected([]);
      setDragStart(null);
      setDragEnd(null);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    // Touch support
    window.addEventListener("touchmove", handleMove);
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [dragStart, selected, game, rows, cols, clearEffect, gridScale, leftBuffer]);

  // keep ref in sync with state
  useEffect(() => { flyingTilesRef.current = flyingTiles; }, [flyingTiles]);

  // Animation loop for flying tiles (simple physics) - stable step stored in ref
  useEffect(() => {
    const gravity = 600 * speedMultiplier * speedMultiplier; // scaled to preserve trajectory shape across speed multiplier
    const outerHeight = rows * (CELL_SIZE + CELL_MARGIN);
    const step = (t) => {
      if (!lastTimeRef.current) lastTimeRef.current = t;
      const dt = Math.min(0.06, (t - lastTimeRef.current) / 1000); // cap dt
      lastTimeRef.current = t;
      let next = [];
      // use current ref value
      const current = flyingTilesRef.current || [];
      next = current.map((f) => {
        const nx = (f.x || 0) + (f.vx || 0) * dt;
        const ny = (f.y || 0) + (f.vy || 0) * dt + 0.5 * gravity * dt * dt;
        const nvx = f.vx || 0;
        const nvy = (f.vy || 0) + gravity * dt;
        const nrot = (f.rot || 0) + (f.rotSpeed || 0) * dt;
        const globalY = (f.top || 0) + ny;
        if (globalY > outerHeight + 300) {
          return null;
        }
        return { ...f, x: nx, y: ny, vx: nvx, vy: nvy, rot: nrot };
      }).filter(Boolean);
      // write back
      setFlyingTiles(next);
      flyingTilesRef.current = next;
      if (next.length > 0) {
        rafRef.current = requestAnimationFrame(stepRef.current || step);
      } else {
        rafRef.current = null;
        lastTimeRef.current = null;
      }
    };
    stepRef.current = step;
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTimeRef.current = null;
      stepRef.current = null;
    };
  }, [rows]);

  // starter: kick the loop when tiles appear
  useEffect(() => {
    if (flyingTiles.length > 0 && !rafRef.current) {
      lastTimeRef.current = null;
      rafRef.current = requestAnimationFrame(stepRef.current);
    }
  }, [flyingTiles]);

  const handleGridMouseDown = (e) => {
    if (!timerActive || timer === 0) return;
    const rect = gridRef.current.getBoundingClientRect();
    const scale = (gridScale || 100) / 100;
    draggingRef.current = true;
    setDragStart([(e.clientX - rect.left - leftBuffer) / scale, (e.clientY - rect.top - CLICK_BUFFER) / scale]);
    setDragEnd([(e.clientX - rect.left - leftBuffer) / scale, (e.clientY - rect.top - CLICK_BUFFER) / scale]);
    setSelected([]);
  };

  // Seed input
  const handleSeedChange = (e) => {
    // Only allow alphanumeric, up to 10 chars
    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 10);
    setSeedInput(val);
  };

  // Zen mode toggle
  const handleZenToggle = () => {
    setZen((z) => !z);
    setTimerActive(false);
  };

  // Start game
  const handlePlay = () => {
    // Only use alphanumeric, up to 10 chars for seed
    const cleanSeed = (seedInput || "").replace(/[^a-zA-Z0-9]/g, "").slice(0, 10) || null;
    setGame(new TensaiGame({ rows, cols, seed: cleanSeed }));
    setTimer(timerSeconds);
    setTimerActive(true);
    setSelected([]);
    prevScoreRef.current = 0;
    endSentRef.current = false;
  };

  // Render grid
  const scale = (gridScale || 100) / 100;
  const outerWidth = cols * (CELL_SIZE + CELL_MARGIN) * scale;
  const outerHeight = rows * (CELL_SIZE + CELL_MARGIN) * scale;
  const wrapperWidth = outerWidth + leftBuffer + rightBuffer;
  const wrapperHeight = outerHeight + CLICK_BUFFER * 2;

  const skin = getSkin(tileSkinId);

  return (
    <div style={{ background: COLORS.bg, padding: 20, borderRadius: 12, width: outerWidth + 40 + leftBuffer + rightBuffer }}>
      {/* Menu */}
      {!timerActive && timer === timerSeconds && (
        <div className="tensai-menu">
          <h1>Tensai</h1>
          <button className="tensai-play-btn" onClick={handlePlay}>Play</button>
          <div className="tensai-seed-group">
            <label className="tensai-label">Seed:</label>
            <input value={seedInput} onChange={handleSeedChange} className="tensai-seed-input" maxLength={10} />
          </div>
          <div className="tensai-zen-group">
            <label className="tensai-label">Zen Mode:</label>
            <input type="checkbox" checked={zen} onChange={handleZenToggle} className="tensai-zen-checkbox" />
          </div>
        </div>
      )}
      {/* Game UI */}
      {(timerActive || timer < timerSeconds) && (
        <div>
          <div className="tensai-score-row">
            <span className="tensai-score-text">Score: {game.score}</span>
          </div>
          {/* Timer bar */}
          {!zen && (
            <div className="tensai-timer-bar">
              {timer === 0 ? (
                <span className="tensai-timer-text--expired">
                  Time's Up!
                </span>
              ) : (
                <span className="tensai-timer-text">
                  {timer}s
                </span>
              )}
              <div style={{ height: "100%", width: `${(timer / timerSeconds) * 100}%`, background: COLORS.timer, borderRadius: 8, transition: "width 0.5s", position: "absolute", left: 0, top: 0, zIndex: 0 }} />
            </div>
          )}
          {/* Grid */}
          <div
            ref={gridRef}
            onMouseDown={handleGridMouseDown}
            style={{
              position: "relative",
              width: wrapperWidth,
              height: wrapperHeight,
              margin: "16px 0",
              userSelect: "none",
              paddingLeft: leftBuffer,
              paddingRight: rightBuffer,
              paddingTop: CLICK_BUFFER,
              paddingBottom: CLICK_BUFFER,
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                width: cols * (CELL_SIZE + CELL_MARGIN),
                height: rows * (CELL_SIZE + CELL_MARGIN),
                position: 'relative',
              }}
            >
              {game.grid.map((row, r) =>
                row.map((cell, c) => {
                  if (!cell && hideEmptyTiles) return null;
                  const isSelected = selected.some(([rr, cc]) => rr === r && cc === c);
                  const tileStyle = skin.renderTile(cell, { selected: isSelected, timerActive, timer, CELL_SIZE, COLORS });
                  return (
                    <div
                      key={`${r}-${c}`}
                      style={{
                        ...tileStyle,
                        left: c * (CELL_SIZE + CELL_MARGIN),
                        top: r * (CELL_SIZE + CELL_MARGIN),
                      }}
                    >
                      {skin.renderContent ? skin.renderContent(cell) : (cell || "")}
                    </div>
                  );
                })
              )}
              {/* Flying tiles animation */}
              {flyingTiles.map((t) => {
                const flyStyle = skin.renderFlyingTile
                  ? skin.renderFlyingTile(t.value, { CELL_SIZE, COLORS })
                  : { width: CELL_SIZE, height: CELL_SIZE, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: '#fff', borderRadius: 8, border: '2px solid #fff', background: COLORS.selected };
                return (
                  <div
                    key={t.id}
                    style={{
                      ...flyStyle,
                      position: 'absolute',
                      left: (t.left || 0) + (t.x || 0),
                      top: (t.top || 0) + (t.y || 0),
                      pointerEvents: 'none',
                      zIndex: 20,
                      transform: `rotate(${t.rot || 0}deg)`,
                      opacity: 1,
                    }}
                  >
                    {skin.renderContent ? skin.renderContent(t.value) : t.value}
                  </div>
                );
              })}
              {/* Drag highlight rectangle */}
              {dragStart && dragEnd && (
                <div
                  style={{
                    position: "absolute",
                    left: Math.min(dragStart[0], dragEnd[0]),
                    top: Math.min(dragStart[1], dragEnd[1]),
                    width: Math.abs(dragEnd[0] - dragStart[0]),
                    height: Math.abs(dragEnd[1] - dragStart[1]),
                    border: `2px solid ${COLORS.selected}`,
                    background: "rgba(96,180,60,0.08)",
                    pointerEvents: "none",
                    zIndex: 10,
                  }}
                />
              )}
            </div>
          </div>
          {/* Timer expired overlay removed; now handled in timer bar */}
        </div>
      )}
    </div>
  );
}

export default Tensai;
